<template>
    <div class="knowledge-graph-3d">
        <div ref="tagsContainer" class="tags-container"></div>
        <div ref="groupsContainer" class="groups-container"></div>
        <div ref="graphContainer" class="graph-container-3d"></div>

        <!-- 右键菜单 -->
        <ContextMenu :visible="contextMenu?.visible" :x="contextMenu?.x" :y="contextMenu?.y"
            :node="selectedNodeForContextMenu" :groups="store?.groups" :graphTypes="store?.graphTypes"
            @set-as-source-node="setAsSourceNode" @set-as-target-node="setAsTargetNode" @add-tag-to-node="addTagToNode"
            @delete-node="deleteNode" @expand-node="expandNode" @edit-node="editNode" @toggle-node-lock="toggleNodeLock"
            @set-as-tag="setAsTag" @toggle-node-group="toggleNodeGroup" @handle-tag-selection="handleTagSelection"
            @close-menu="closeContextMenu" :key="contextMenuKey" />
    </div>
</template>

<script>
import { ref, onMounted, onUnmounted, watch, nextTick } from 'vue'
import * as THREE from 'three'
import { OrbitControls } from 'three/examples/jsm/controls/OrbitControls.js'
import { useKnowledgeGraphStore } from '../stores/knowledgeGraphStore.js'
import ContextMenu from './ContextMenu.vue'

export default {
    name: 'KnowledgeGraph3D',
    components: {
        ContextMenu
    },
    emits: ['node-click', 'edge-click', 'canvas-click', 'node-drag', 'node-edit', 'edge-edit', 'node-lock', 'node-highlight', 'add-relationship', 'delete-node', 'expand-node', 'delete-relationship'],
    setup(props, { emit }) {
        const graphContainer = ref(null)
        const tagsContainer = ref(null)
        const groupsContainer = ref(null)
        const store = useKnowledgeGraphStore()

        // 右键菜单相关数据
        const contextMenu = ref({
            visible: false,
            x: 0,
            y: 0
        });

        // 用于强制更新ContextMenu组件的key
        const contextMenuKey = ref(0);

        // 用于存储右键点击的节点
        const selectedNodeForContextMenu = ref(null);



        // Three.js相关变量
        let scene = null
        let camera = null
        let renderer = null
        let controls = null
        let raycaster = new THREE.Raycaster()
        let mouse = new THREE.Vector2()

        // 存储节点和边的映射
        let nodeMeshes = new Map()
        let edgeLines = new Map()

        // 初始化3D场景
        const initScene = () => {
            if (!graphContainer.value) return

            // 创建场景
            scene = new THREE.Scene()
            scene.background = new THREE.Color(0xf5f7fa)

            // 创建相机
            const containerWidth = graphContainer.value.clientWidth
            const containerHeight = graphContainer.value.clientHeight
            camera = new THREE.PerspectiveCamera(75, containerWidth / containerHeight, 0.1, 10000)
            camera.position.z = 100

            // 创建渲染器
            renderer = new THREE.WebGLRenderer({ antialias: true })
            renderer.setSize(containerWidth, containerHeight)
            renderer.setPixelRatio(window.devicePixelRatio)
            // 设置渲染器DOM元素的样式，确保菜单可以显示在其上方
            renderer.domElement.style.position = 'relative';
            renderer.domElement.style.zIndex = '1';
            renderer.domElement.style.display = 'block';
            graphContainer.value.appendChild(renderer.domElement)

            // 添加光源
            const ambientLight = new THREE.AmbientLight(0x404040, 1)
            scene.add(ambientLight)

            const directionalLight = new THREE.DirectionalLight(0xffffff, 1)
            directionalLight.position.set(1, 1, 1)
            scene.add(directionalLight)

            const pointLight = new THREE.PointLight(0xffffff, 1, 1000)
            pointLight.position.set(0, 0, 100)
            scene.add(pointLight)

            // 添加轨道控制器
            controls = new OrbitControls(camera, renderer.domElement)
            controls.enableDamping = true
            controls.dampingFactor = 0.05
            controls.screenSpacePanning = false
            controls.minDistance = 10
            controls.maxDistance = 1000

            // 添加事件监听
            renderer.domElement.addEventListener('click', onMouseClick, false)
            renderer.domElement.addEventListener('mousemove', onMouseMove, false)
            renderer.domElement.addEventListener('mousedown', onMouseDown, false)
            renderer.domElement.addEventListener('mousemove', onMouseMoveForDrag, false)
            renderer.domElement.addEventListener('mouseup', onMouseUp, false)
            renderer.domElement.addEventListener('mouseleave', onMouseUp, false)

            // 初始化右键菜单事件
            initRightClickEvent();

            // 开始渲染循环
            animate()
        }

        // 为节点添加拖拽功能
        let selectedNode3D = null
        let offset = new THREE.Vector3()
        let isDragging = false
        let dragStartPosition = null  // 记录拖拽起始位置，用于区分点击和拖拽

        // 处理鼠标按下事件（用于拖拽）
        const onMouseDown = (event) => {
            // 只处理左键点击，右键点击由contextmenu事件处理
            if (event.button !== 0) return;

            // 计算鼠标在标准化设备坐标中的位置 (-1 到 +1)
            mouse.x = (event.clientX - renderer.domElement.getBoundingClientRect().left) / renderer.domElement.clientWidth * 2 - 1
            mouse.y = -(event.clientY - renderer.domElement.getBoundingClientRect().top) / renderer.domElement.clientHeight * 2 + 1

            // 更新射线
            raycaster.setFromCamera(mouse, camera)

            // 检测与节点的交点
            const intersects = raycaster.intersectObjects(Array.from(nodeMeshes.values()))

            if (intersects.length > 0) {
                const clickedMesh = intersects[0].object
                selectedNode3D = clickedMesh

                // 记录拖拽起始位置，用于区分点击和拖拽
                dragStartPosition = { x: event.clientX, y: event.clientY };

                // 计算点击点相对于节点的偏移
                offset.copy(clickedMesh.position)
                offset.sub(getMouseWorldPosition(mouse.x, mouse.y))

                isDragging = true

                // 阻止事件冒泡
                event.preventDefault()
            }
        }

        // 处理鼠标移动事件（用于拖拽）
        const onMouseMoveForDrag = (event) => {
            if (!isDragging || !selectedNode3D) return

            mouse.x = (event.clientX - renderer.domElement.getBoundingClientRect().left) / renderer.domElement.clientWidth * 2 - 1
            mouse.y = -(event.clientY - renderer.domElement.getBoundingClientRect().top) / renderer.domElement.clientHeight * 2 + 1

            const worldPosition = getMouseWorldPosition(mouse.x, mouse.y)
            worldPosition.add(offset)

            selectedNode3D.position.copy(worldPosition)

            // 更新相关的边
            updateRelatedEdges(selectedNode3D.userData.id)
        }

        // 根据鼠标位置计算世界坐标
        const getMouseWorldPosition = (mouseX, mouseY) => {
            raycaster.setFromCamera(new THREE.Vector2(mouseX, mouseY), camera)

            // 创建一个平面，用于计算交点
            const plane = new THREE.Plane(new THREE.Vector3(0, 0, 1), 0)
            const intersection = new THREE.Vector3()
            raycaster.ray.intersectPlane(plane, intersection)

            return intersection
        }

        // 更新与节点相关的边
        const updateRelatedEdges = (nodeId) => {
            const nodeMesh = nodeMeshes.get(nodeId)
            if (!nodeMesh) return

            // 遍历所有边，更新与当前节点相关的边
            store.graphData.edges.forEach(edge => {
                if (edge.source === nodeId || edge.target === nodeId) {
                    const edgeLine = edgeLines.get(edge.id)
                    if (edgeLine) {
                        const sourceNode = nodeMeshes.get(edge.source)
                        const targetNode = nodeMeshes.get(edge.target)

                        if (sourceNode && targetNode) {
                            const geometry = new THREE.BufferGeometry()
                            const points = [
                                new THREE.Vector3(sourceNode.position.x, sourceNode.position.y, sourceNode.position.z),
                                new THREE.Vector3(targetNode.position.x, targetNode.position.y, targetNode.position.z)
                            ]
                            geometry.setFromPoints(points)
                            edgeLine.geometry = geometry
                        }
                    }
                }
            })
        }

        // 渲染循环
        const animate = () => {
            requestAnimationFrame(animate)

            if (controls) {
                controls.update()
            }

            if (renderer && scene && camera) {
                renderer.render(scene, camera)
            }

            // 更新标签位置
            updateLabels();
        }

        // 处理鼠标点击事件（主要用于检测空白区域点击）
        const onMouseClick = (event) => {
            // 只处理左键点击，右键点击由contextmenu事件处理
            if (event.button !== 0) return;

            // 我们不在此处理节点点击，因为节点点击由拖拽系统处理
            // 这里主要用于检测是否点击了空白区域

            // 计算鼠标在标准化设备坐标中的位置 (-1 到 +1)
            mouse.x = (event.clientX - renderer.domElement.getBoundingClientRect().left) / renderer.domElement.clientWidth * 2 - 1
            mouse.y = -(event.clientY - renderer.domElement.getBoundingClientRect().top) / renderer.domElement.clientHeight * 2 + 1

            // 更新射线
            raycaster.setFromCamera(mouse, camera)

            // 检测与节点的交点
            const intersects = raycaster.intersectObjects(Array.from(nodeMeshes.values()))
            // 如果没有与任何节点相交，则认为是点击了空白区域
            if (intersects.length === 0) {
                emit('canvas-click')
            }
        }

        // 处理鼠标抬起事件（结束拖拽或处理点击）
        const onMouseUp = (event) => {
            // 只处理左键点击，右键点击由contextmenu事件处理
            if (event && event.button !== 0) return;

            if (isDragging && selectedNode3D) {
                const nodeId = selectedNode3D.userData.id
                const node = store.graphData.nodes.find(n => n.id === nodeId)

                // 检查鼠标移动距离，如果移动很小则认为是点击而非拖拽
                if (dragStartPosition && event) {
                    const moveThreshold = 5; // 像素阈值，超过此距离才认为是拖拽
                    const movedDistance = Math.sqrt(
                        Math.pow(event.clientX - dragStartPosition.x, 2) +
                        Math.pow(event.clientY - dragStartPosition.y, 2)
                    );

                    if (movedDistance <= moveThreshold) {
                        // 移动距离小，认为是点击
                        isDragging = false; // 不执行拖拽更新

                        // 执行节点点击逻辑
                        if (node) {
                            console.log('3D模式 - 检测到节点点击（通过点击而非拖拽），节点ID:', nodeId);
                            console.log('3D模式 - 检测到节点点击，节点信息:', node);
                            console.log('3D模式 - 准备发出 node-click 事件:', node);
                            emit('node-click', node);
                            console.log('3D模式 - 已发出 node-click 事件');

                            // 但不更新位置，因为这不是拖拽操作
                        }
                    } else {
                        // 移动距离大，认为是拖拽
                        if (node) {
                            // 发送节点拖拽事件，包含3D坐标
                            emit('node-drag', nodeId, {
                                x: selectedNode3D.position.x,
                                y: selectedNode3D.position.y,
                                z: selectedNode3D.position.z
                            })

                            // 更新store中的节点位置
                            store.updateNode(nodeId, {
                                x: selectedNode3D.position.x,
                                y: selectedNode3D.position.y,
                                z: selectedNode3D.position.z
                            })
                        }
                    }
                }
            }

            isDragging = false
            selectedNode3D = null
            dragStartPosition = null; // 重置起始位置
        }

        // 处理鼠标移动事件（用于悬停效果）
        const onMouseMove = (event) => {
            mouse.x = (event.clientX - renderer.domElement.getBoundingClientRect().left) / renderer.domElement.clientWidth * 2 - 1
            mouse.y = -(event.clientY - renderer.domElement.getBoundingClientRect().top) / renderer.domElement.clientHeight * 2 + 1

            raycaster.setFromCamera(mouse, camera)

            const intersects = raycaster.intersectObjects(Array.from(nodeMeshes.values()))

            // 恢复所有节点的原始状态
            nodeMeshes.forEach(mesh => {
                mesh.material.color.set(mesh.userData.originalColor)
                mesh.scale.set(1, 1, 1)
            })

            // 高亮悬停的节点
            if (intersects.length > 0) {
                const hoveredMesh = intersects[0].object
                hoveredMesh.material.color.set(0xff6b35) // 高亮颜色
                hoveredMesh.scale.set(1.2, 1.2, 1.2) // 缩放效果
            }
        }

        // 加载图数据
        const loadGraphData = () => {
            if (!scene) return

            // 清除现有节点和边
            clearScene()

            const data = store.graphData

            // 创建节点
            data.nodes.forEach(node => {
                createNode(node)
            })

            // 创建边
            data.edges.forEach(edge => {
                createEdge(edge)
            })
        }

        // 创建节点
        const createNode = (node) => {
            // 增加球体半径以提高点击检测的准确性
            const geometry = new THREE.SphereGeometry(10, 32, 32)
            const material = new THREE.MeshPhongMaterial({
                color: getNodeColor(node),
                shininess: 100
            })

            const sphere = new THREE.Mesh(geometry, material)

            // 设置节点位置，如果节点没有位置信息则随机分配
            sphere.position.x = node.x || (Math.random() - 0.5) * 200
            sphere.position.y = node.y || (Math.random() - 0.5) * 200
            sphere.position.z = node.z || (Math.random() - 0.5) * 200

            // 保存原始颜色用于悬停效果
            material.userData = {
                originalColor: material.color.getHex(),
                id: node.id
            }

            sphere.userData = {
                id: node.id,
                originalColor: material.color.getHex()
            }

            // 确保对象可以被射线检测
            sphere.name = `node-${node.id}`

            scene.add(sphere)
            nodeMeshes.set(node.id, sphere)

            // 创建节点标签
            createNodeLabel(node, sphere)
        }

        // 创建边
        const createEdge = (edge) => {
            const sourceNode = nodeMeshes.get(edge.source)
            const targetNode = nodeMeshes.get(edge.target)

            if (!sourceNode || !targetNode) return

            const material = new THREE.LineBasicMaterial({
                color: getEdgeColor(edge),
                linewidth: 2
            })

            const geometry = new THREE.BufferGeometry()
            const points = [
                new THREE.Vector3(sourceNode.position.x, sourceNode.position.y, sourceNode.position.z),
                new THREE.Vector3(targetNode.position.x, targetNode.position.y, targetNode.position.z)
            ]
            geometry.setFromPoints(points)

            const line = new THREE.Line(geometry, material)
            scene.add(line)

            edgeLines.set(edge.id, line)
        }

        // 获取节点颜色
        const getNodeColor = (node) => {
            // 使用节点的分组或类别信息来确定颜色
            if (node.style && node.style.fill) {
                return new THREE.Color(node.style.fill)
            }

            // 使用默认颜色方案
            const colorIndex = node.groups ? node.groups[0] : 'default'
            const hash = Math.abs(colorIndex.toString().split('').reduce((a, b) => ((a << 5) - a + b.charCodeAt(0)) | 0, 0))
            const colors = [
                0xFF6B6B, 0x4ECDC4, 0x45B7D1, 0x96CEB4, 0xFFEAA7,
                0xDDA0DD, 0x98D8C8, 0xF7DC6F, 0xBB8FCE, 0x85C1E9
            ]
            return colors[hash % colors.length]
        }

        // 获取边颜色
        const getEdgeColor = (edge) => {
            if (edge.graphId) {
                // 使用graphId生成颜色
                const hash = Math.abs(edge.graphId.toString().split('').reduce((a, b) => ((a << 5) - a + b.charCodeAt(0)) | 0, 0))
                const colors = [
                    0xFF6B6B, 0x4ECDC4, 0x45B7D1, 0x96CEB4, 0xFFEAA7,
                    0xDDA0DD, 0x98D8C8, 0xF7DC6F, 0xBB8FCE, 0x85C1E9
                ]
                return colors[hash % colors.length]
            }
            return 0x999999
        }

        // 创建节点标签
        let labelDivs = new Map(); // 存储标签DOM元素

        const createNodeLabel = (node) => {
            // 创建HTML标签元素
            const label = document.createElement('div');
            label.className = 'node-label-3d';
            label.textContent = node.name || node.label || '';
            label.style.position = 'absolute';
            label.style.top = '0';
            label.style.left = '0';
            label.style.padding = '4px 8px';
            label.style.borderRadius = '4px';
            label.style.backgroundColor = 'rgba(0, 0, 0, 0.7)';
            label.style.color = 'white';
            label.style.fontSize = '12px';
            label.style.whiteSpace = 'nowrap';
            label.style.pointerEvents = 'none';
            label.style.zIndex = '1000';
            label.style.display = 'none'; // 默认隐藏，通过CSS控制显示

            // 添加唯一ID
            label.id = `node-label-${node.id}`;

            // 添加到容器
            if (graphContainer.value) {
                graphContainer.value.appendChild(label);

                // 将标签与节点关联
                labelDivs.set(node.id, label);
            }
        };

        // 更新所有标签位置
        const updateLabels = () => {
            if (!camera || !graphContainer.value) return;

            // 遍历所有节点，更新标签位置
            nodeMeshes.forEach((mesh, nodeId) => {
                const label = labelDivs.get(nodeId);
                if (label) {
                    // 将3D坐标转换为屏幕坐标
                    const vector = mesh.position.clone();
                    vector.project(camera);

                    const containerRect = graphContainer.value.getBoundingClientRect();
                    const halfWidth = containerRect.width / 2;
                    const halfHeight = containerRect.height / 2;

                    // 计算屏幕坐标
                    const x = Math.round(vector.x * halfWidth + halfWidth);
                    const y = Math.round(-vector.y * halfHeight + halfHeight);

                    // 检查节点是否在视野内
                    if (vector.z > 1 || vector.z < -1) {
                        // 节点不在视野内，隐藏标签
                        label.style.display = 'none';
                    } else {
                        // 设置标签位置
                        label.style.display = 'block';
                        label.style.left = `${x}px`;
                        label.style.top = `${y}px`;

                        // 添加偏移以使标签位于节点上方
                        label.style.transform = 'translate(-50%, -100%)';
                    }
                }
            });
        }

        // 更新所有边
        const updateAllEdges = () => {
            // 遍历所有边并更新它们的位置
            edgeLines.forEach((line, edgeId) => {
                const edge = store.graphData.edges.find(e => e.id === edgeId);
                if (edge) {
                    const sourceNode = nodeMeshes.get(edge.source);
                    const targetNode = nodeMeshes.get(edge.target);

                    if (sourceNode && targetNode) {
                        const geometry = new THREE.BufferGeometry();
                        const points = [
                            new THREE.Vector3(
                                sourceNode.position.x,
                                sourceNode.position.y,
                                sourceNode.position.z
                            ),
                            new THREE.Vector3(
                                targetNode.position.x,
                                targetNode.position.y,
                                targetNode.position.z
                            )
                        ];
                        geometry.setFromPoints(points);
                        line.geometry = geometry;
                    }
                }
            });
        };

        // 清除场景
        const clearScene = () => {
            // 移除所有节点
            nodeMeshes.forEach((mesh) => {
                scene.remove(mesh)
            })
            nodeMeshes.clear()

            // 移除所有边
            edgeLines.forEach((line) => {
                scene.remove(line)
            })
            edgeLines.clear()

            // 移除所有标签元素
            labelDivs.forEach((label) => {
                if (label.parentNode) {
                    label.parentNode.removeChild(label);
                }
            });
            labelDivs.clear();
        }

        // 适应视图
        const fitView = () => {
            if (!camera || !controls) return

            // 计算所有节点的边界框
            const box = new THREE.Box3()
            nodeMeshes.forEach(mesh => {
                box.expandByObject(mesh)
            })

            if (box.isEmpty()) return

            // 获取边界框的中心和大小
            const center = box.getCenter(new THREE.Vector3())
            const size = box.getSize(new THREE.Vector3())
            const maxSize = Math.max(size.x, size.y, size.z)

            // 设置相机位置
            const fitHeightDistance = maxSize / (2 * Math.atan((Math.PI * camera.fov) / 360))
            const fitWidthDistance = fitHeightDistance / camera.aspect
            const distance = Math.max(fitHeightDistance, fitWidthDistance, 10) * 1.5

            camera.position.copy(center)
            camera.position.x += distance
            camera.position.y += distance
            camera.position.z += distance
            camera.lookAt(center)

            // 更新控制器
            controls.update()
        }

        // 重置布局
        const resetLayout = () => {
            // 重新随机分配节点位置
            nodeMeshes.forEach((mesh, id) => {
                const node = store.graphData.nodes.find(n => n.id === id)
                if (node) {
                    node.x = (Math.random() - 0.5) * 200
                    node.y = (Math.random() - 0.5) * 200
                    node.z = (Math.random() - 0.5) * 200

                    mesh.position.x = node.x
                    mesh.position.y = node.y
                    mesh.position.z = node.z
                }
            })

            // 更新关系线以匹配新的节点位置
            updateAllEdges();
        }

        // 放大
        const zoomIn = () => {
            if (camera && controls) {
                camera.position.multiplyScalar(0.8)
                controls.update()
            }
        }

        // 缩小
        const zoomOut = () => {
            if (camera && controls) {
                camera.position.multiplyScalar(1.2)
                controls.update()
            }
        }

        // 选择节点
        const selectNode = (nodeId) => {
            // 在3D视图中，我们通过改变节点颜色来表示选择
            nodeMeshes.forEach(mesh => {
                // 恢复原始颜色
                const originalColor = mesh.userData.originalColor
                mesh.material.color.set(originalColor)

                // 如果是选中的节点，改变颜色
                if (mesh.userData.id === nodeId) {
                    mesh.material.color.set(0xff6b35) // 选中颜色
                }
            })
        }

        // 高亮相关节点
        const highlightRelatedNodes = (nodeId) => {
            // 高亮选中的节点
            nodeMeshes.forEach(mesh => {
                const originalColor = mesh.userData.originalColor
                mesh.material.color.set(originalColor)

                // 如果是选中的节点，使用选中颜色
                if (mesh.userData.id === nodeId) {
                    mesh.material.color.set(0xff6b35) // 选中颜色
                }
            })

            // 高亮相关的节点
            const relatedNodeIds = new Set()
            store.graphData.edges.forEach(edge => {
                if (edge.source === nodeId) {
                    relatedNodeIds.add(edge.target)
                } else if (edge.target === nodeId) {
                    relatedNodeIds.add(edge.source)
                }
            })

            relatedNodeIds.forEach(relatedId => {
                const relatedMesh = nodeMeshes.get(relatedId)
                if (relatedMesh) {
                    relatedMesh.material.color.set(0x61DDAA) // 相关节点颜色
                }
            })
        }

        // 调整大小
        const resize = () => {
            if (!graphContainer.value || !camera || !renderer) return

            const width = graphContainer.value.clientWidth
            const height = graphContainer.value.clientHeight

            camera.aspect = width / height
            camera.updateProjectionMatrix()

            renderer.setSize(width, height)
        }

        // 全屏切换
        const toggleFullscreen = () => {
            if (!graphContainer.value) return;

            if (!document.fullscreenElement) {
                graphContainer.value.requestFullscreen().catch(err => {
                    console.error(`进入全屏模式失败: ${err.message}`);
                });
            } else {
                document.exitFullscreen().catch(err => {
                    console.error(`退出全屏模式失败: ${err.message}`);
                });
            }
        }

        // 处理节点右键点击事件
        const handleNodeRightClick = (event, node) => {
            console.log('节点右键点击:', node)
            event.preventDefault() // 阻止默认右键菜单
            event.stopPropagation()

            // 存储右键点击的节点
            selectedNodeForContextMenu.value = node;
            console.log('右键点击的节点:', selectedNodeForContextMenu.value);

            // 设置右键菜单位置
            contextMenu.value.x = event.clientX;
            contextMenu.value.y = event.clientY;
            contextMenu.value.visible = true;

            // 强制更新ContextMenu组件
            contextMenuKey.value++;

            console.log('节点右键点击，contextMenu设置为可见:', contextMenu.value);

            // 显示原生右键菜单作为备选方案
            showNativeContextMenu(event.clientX, event.clientY, node);
        }

        // 显示原生右键菜单
        const showNativeContextMenu = (x, y, node) => {
            // 移除可能已存在的菜单
            const existingMenu = document.getElementById('native-context-menu');
            if (existingMenu) {
                existingMenu.remove();
            }

            // 创建菜单元素
            const menu = document.createElement('div');
            menu.id = 'native-context-menu';
            menu.style.position = 'fixed';
            menu.style.left = x + 'px';
            menu.style.top = y + 'px';
            menu.style.backgroundColor = 'white';
            menu.style.border = '1px solid #ccc';
            menu.style.borderRadius = '4px';
            menu.style.boxShadow = '0 4px 20px rgba(0, 0, 0, 0.3)';
            menu.style.zIndex = '99999';
            menu.style.minWidth = '180px';
            menu.style.padding = '10px';
            menu.style.fontFamily = 'Arial, sans-serif';

            // 添加菜单项
            const menuItems = [
                { text: '设为源节点', action: 'setAsSourceNode' },
                { text: '设为目标节点', action: 'setAsTargetNode' },
                { text: '添加标签', action: 'addTagToNode' },
                { text: '删除节点', action: 'deleteNode' },
                { text: '拓展节点', action: 'expandNode' },
                { text: '编辑节点', action: 'editNode' },
                { text: node?.locked ? '解锁节点' : '锁定节点', action: 'toggleNodeLock' }
            ];

            menuItems.forEach(item => {
                const menuItem = document.createElement('div');
                menuItem.style.padding = '8px';
                menuItem.style.cursor = 'pointer';
                menuItem.style.borderBottom = '1px solid #eee';
                menuItem.style.display = 'flex';
                menuItem.style.alignItems = 'center';
                menuItem.style.gap = '8px';
                menuItem.textContent = item.text;

                menuItem.addEventListener('click', () => {
                    // 根据菜单项执行相应操作
                    switch (item.action) {
                        case 'setAsSourceNode':
                            setAsSourceNode();
                            break;
                        case 'setAsTargetNode':
                            setAsTargetNode();
                            break;
                        case 'addTagToNode':
                            addTagToNode();
                            break;
                        case 'deleteNode':
                            deleteNode();
                            break;
                        case 'expandNode':
                            expandNode();
                            break;
                        case 'editNode':
                            editNode();
                            break;
                        case 'toggleNodeLock':
                            toggleNodeLock();
                            break;
                    }
                    // 移除菜单
                    menu.remove();
                });

                menu.appendChild(menuItem);
            });

            // 添加点击外部区域关闭菜单的功能
            const handleClickOutside = (event) => {
                if (!menu.contains(event.target)) {
                    menu.remove();
                    document.removeEventListener('click', handleClickOutside);
                }
            };

            document.addEventListener('click', handleClickOutside);

            // 添加到body
            document.body.appendChild(menu);

            console.log('原生右键菜单已创建并显示');
        }

        // 添加鼠标右键事件监听
        const initRightClickEvent = () => {
            if (!renderer || !renderer.domElement) return;

            renderer.domElement.addEventListener('contextmenu', (event) => {
                console.log('3D场景右键菜单事件触发', event);
                event.preventDefault(); // 阻止浏览器默认右键菜单

                // 计算鼠标在标准化设备坐标中的位置 (-1 到 +1)
                mouse.x = (event.clientX - renderer.domElement.getBoundingClientRect().left) / renderer.domElement.clientWidth * 2 - 1
                mouse.y = -(event.clientY - renderer.domElement.getBoundingClientRect().top) / renderer.domElement.clientHeight * 2 + 1

                // 更新射线
                raycaster.setFromCamera(mouse, camera)

                // 检测与节点的交点
                const intersects = raycaster.intersectObjects(Array.from(nodeMeshes.values()))
                console.log('射线检测结果，交点数量:', intersects.length);

                if (intersects.length > 0) {
                    const clickedMesh = intersects[0].object
                    console.log('点击的mesh:', clickedMesh);
                    const nodeId = clickedMesh.userData.id
                    console.log('点击的节点ID:', nodeId);

                    // 查找对应的节点数据
                    const node = store.graphData.nodes.find(n => n.id === nodeId)
                    console.log('找到的节点:', node);

                    if (node) {
                        // 触发节点右键点击事件
                        handleNodeRightClick(event, node)
                    }
                } else {
                    // 点击空白区域，也显示菜单（但可能没有特定节点操作）
                    console.log('点击空白区域，显示菜单');
                    event.preventDefault();
                    contextMenu.value.x = event.clientX;
                    contextMenu.value.y = event.clientY;
                    contextMenu.value.visible = true;

                    // 强制更新ContextMenu组件
                    contextMenuKey.value++;

                    console.log('contextMenu设置为可见:', contextMenu.value);
                }
            });
        }

        // 设为源节点
        const setAsSourceNode = () => {
            if (selectedNodeForContextMenu.value) {
                // 在3D图中，我们可能需要通过emit将节点传递给父组件来处理
                emit('set-source-node', selectedNodeForContextMenu.value);
                contextMenu.value.visible = false;
                console.log('设为源节点:', selectedNodeForContextMenu.value);
            }
        }

        // 设为目标节点
        const setAsTargetNode = () => {
            if (selectedNodeForContextMenu.value) {
                emit('add-relationship', {
                    source: store.selectedNode, // 假设store中有一个selectedNode
                    target: selectedNodeForContextMenu.value
                })
                contextMenu.value.visible = false;
                console.log('设为目标节点:', selectedNodeForContextMenu.value);
            }
        }

        // 添加标签到节点
        const addTagToNode = () => {
            if (selectedNodeForContextMenu.value) {
                const tag = prompt('请输入标签名称:');
                if (tag) {
                    console.log('为节点添加标签:', tag, selectedNodeForContextMenu.value);
                    // 触发添加标签事件
                    emit('add-tag', selectedNodeForContextMenu.value.id, tag);
                }
                contextMenu.value.visible = false;
            }
        }

        // 更新群组菜单状态
        const updateGroupMenu = () => {
            // 触发响应式更新
            if (selectedNodeForContextMenu.value) {
                nextTick(() => {
                    // 重新计算群组状态
                })
            }
        }

        // 更新标签菜单状态
        const updateTagMenu = () => {
            // 触发响应式更新
            if (selectedNodeForContextMenu.value) {
                nextTick(() => {
                    // 重新计算标签状态
                })
            }
        }

        // 判断节点是否在指定group中
        const isNodeInGroup = (node, groupId) => {
            if (!node || !node.groups || !Array.isArray(node.groups)) return false;
            return node.groups.includes(groupId);
        }

        // 判断节点是否具有指定标签
        const isNodeInTag = (tagId) => {
            if (!selectedNodeForContextMenu.value || !selectedNodeForContextMenu.value.category) return false;

            // 检查节点的category属性是否包含该标签
            // category可能是数组或单个值
            if (Array.isArray(selectedNodeForContextMenu.value.category)) {
                return selectedNodeForContextMenu.value.category.includes(tagId);
            } else {
                return selectedNodeForContextMenu.value.category === tagId;
            }
        }

        // 切换节点group
        const toggleNodeGroup = (groupId) => {
            if (!selectedNodeForContextMenu.value) return;

            // 确保节点有groups属性
            if (!selectedNodeForContextMenu.value.groups) {
                selectedNodeForContextMenu.value.groups = [];
            }

            // 创建节点groups的副本
            const currentGroups = [...selectedNodeForContextMenu.value.groups];
            const groupIndex = currentGroups.indexOf(groupId);

            if (groupIndex > -1) {
                // 如果节点已在该group中，则移除
                currentGroups.splice(groupIndex, 1);
            } else {
                // 如果节点不在该group中，则添加
                currentGroups.push(groupId);
            }

            // 更新节点的groups
            selectedNodeForContextMenu.value.groups = currentGroups;

            // 更新store中的节点数据
            store.updateNode(selectedNodeForContextMenu.value.id, { groups: currentGroups });

            // 隐藏菜单
            contextMenu.value.visible = false;
        }

        // 处理标签选择
        const handleTagSelection = async (tag) => {
            if (selectedNodeForContextMenu.value && tag) {
                console.log('选中的标签:', tag.name || tag.label);
                // 调用addTagToNode方法为节点添加标签
                try {
                    // 更新节点的category属性以反映新添加的标签
                    if (!selectedNodeForContextMenu.value.category) {
                        selectedNodeForContextMenu.value.category = [];
                    }
                    // 如果category还不是数组，将其转换为数组
                    if (!Array.isArray(selectedNodeForContextMenu.value.category)) {
                        selectedNodeForContextMenu.value.category = [selectedNodeForContextMenu.value.category];
                    }
                    // 将标签ID添加到节点的category中（如果尚未存在）
                    if (!selectedNodeForContextMenu.value.category.includes(tag.id)) {
                        selectedNodeForContextMenu.value.category.push(tag.id);
                    }
                    // 更新store中的节点数据
                    store.updateNode(selectedNodeForContextMenu.value.id, {
                        category: selectedNodeForContextMenu.value.category
                    });
                } catch (error) {
                    console.error('添加标签时发生错误:', error);
                }
                contextMenu.value.visible = false;
            }
        }

        // 删除节点
        const deleteNode = () => {
            if (selectedNodeForContextMenu.value) {
                if (confirm(`确定要删除节点 "${selectedNodeForContextMenu.value.name}" 吗?`)) {
                    emit('delete-node', selectedNodeForContextMenu.value.id);
                    contextMenu.value.visible = false;
                }
            }
        }

        // 拓展节点
        const expandNode = () => {
            if (selectedNodeForContextMenu.value) {
                emit('expand-node', selectedNodeForContextMenu.value.id);
                contextMenu.value.visible = false;
            }
        }

        // eslint-disable-next-line no-unused-vars
        const closeContextMenu = () => {
            contextMenu.value.visible = false;
            // 强制更新ContextMenu组件
            contextMenuKey.value++;
        }

        // 编辑节点
        const editNode = () => {
            if (selectedNodeForContextMenu.value) {
                const newLabel = prompt('请输入新的节点标签:', selectedNodeForContextMenu.value.label || selectedNodeForContextMenu.value.name || '')
                if (newLabel !== null && newLabel !== (selectedNodeForContextMenu.value.label || selectedNodeForContextMenu.value.name)) {
                    emit('node-edit', selectedNodeForContextMenu.value.id, newLabel)
                }
                contextMenu.value.visible = false;
            }
        }

        // 锁定/解锁节点
        const toggleNodeLock = () => {
            if (selectedNodeForContextMenu.value) {
                selectedNodeForContextMenu.value.locked = !selectedNodeForContextMenu.value.locked;
                emit('node-lock', selectedNodeForContextMenu.value.id, selectedNodeForContextMenu.value.locked);
                contextMenu.value.visible = false;
            }
        }

        // 设为标签
        const setAsTag = async () => {
            if (selectedNodeForContextMenu.value) {
                const nodeId = selectedNodeForContextMenu.value.id;

                // 准备数据
                const data = {
                    toNodeId: nodeId,
                    fromNodeId: nodeId, // addTag操作时fromNodeId和toNodeId相同
                    changeType: "addTag"
                };

                try {
                    // 调用store的changeGraph方法
                    const response = await store.changeGraph(data);

                    // 检查响应状态
                    if (response && response.status === 200) {
                        // 添加成功，创建新的标签对象
                        const newTag = {
                            id: nodeId,
                            name: selectedNodeForContextMenu.value.name || selectedNodeForContextMenu.value.label || `标签${nodeId}`
                        };

                        // 将新标签添加到store的tags中
                        store.tags.push(newTag);

                        // 触发标签显示更新
                        // initTags(); // 如果有initTags方法

                        console.log('标签添加成功:', newTag);
                    } else {
                        console.error('添加标签失败:', response?.message || '未知错误');
                    }
                } catch (error) {
                    console.error('添加标签时发生错误:', error);
                }

                contextMenu.value.visible = false;
            }
        }

        // 监听数据变化
        watch(
            () => store.dataVersion,
            (newVersion, oldVersion) => {
                if (oldVersion !== undefined && newVersion > oldVersion) {
                    nextTick(() => {
                        loadGraphData()
                    })
                }
            }
        )

        // 监听标签数据变化
        watch(
            () => store.tags,
            (newTags, oldTags) => {
                console.log('检测到标签数据变化，更新标签显示')
                console.log('newTags:', newTags)
                console.log('oldTags:', oldTags)
                nextTick(() => {
                    initTags()
                })
            },
            { deep: true } // 添加深度监听
        )

        // 监听分组数据变化
        watch(
            () => store.groups,
            (newGroups, oldGroups) => {
                console.log('检测到分组数据变化，更新分组显示')
                console.log('newGroups:', newGroups)
                console.log('oldGroups:', oldGroups)
                nextTick(() => {
                    initGroups()
                })
            },
            { deep: true } // 添加深度监听
        )

        // 监听选中标签变化
        watch(
            () => store.selectedTags,
            (newSelectedTags, oldSelectedTags) => {
                console.log('检测到选中标签变化，更新标签显示')
                console.log('newSelectedTags:', newSelectedTags)
                console.log('oldSelectedTags:', oldSelectedTags)
                nextTick(() => {
                    updateTagsDisplay()
                })
            }
        )

        // 监听选中分组变化
        watch(
            () => store.filters.groups,
            (newSelectedGroups, oldSelectedGroups) => {
                console.log('检测到选中分组变化，更新分组显示')
                console.log('newSelectedGroups:', newSelectedGroups)
                console.log('oldSelectedGroups:', oldSelectedGroups)
                nextTick(() => {
                    updateGroupsDisplay()
                })
            }
        )

        // 监听窗口大小变化
        const handleResize = () => {
            resize()
        }

        // 初始化标签显示
        const initTags = () => {
            console.log('开始初始化标签显示')
            console.log('当前store中的标签:', store.tags)

            if (!tagsContainer.value) {
                console.error('Tags container not found')
                return
            }

            // 清除之前的标签元素
            tagsContainer.value.innerHTML = ''

            // 如果没有标签，直接返回
            if (!store.tags || store.tags.length === 0) {
                console.log('没有标签需要显示')
                return
            }

            // 创建标签容器
            const tagsWrapper = document.createElement('div')
            tagsWrapper.className = 'tags-wrapper'

            // 添加标签
            store.tags.forEach((tag, index) => {
                console.log('处理标签:', tag)
                // 确保标签对象有name属性
                if (!tag || !tag.name) {
                    console.warn('标签对象不完整:', tag)
                    return
                }

                const tagElement = document.createElement('span')
                tagElement.className = 'tag-item'
                tagElement.textContent = tag.name
                tagElement.dataset.tagId = tag.id || index
                tagElement.dataset.tagName = tag.name

                // 设置标签颜色
                const customColorScheme = [
                    '#FF6B6B', '#4ECDC4', '#45B7D1', '#96CEB4', '#FFEAA7',
                    '#DDA0DD', '#98D8C8', '#F7DC6F', '#BB8FCE', '#85C1E9',
                    '#F8C471', '#82E0AA', '#F1948A', '#85C1E9', '#D7BDE2'
                ]
                const colorIndex = index % customColorScheme.length
                tagElement.dataset.color = customColorScheme[colorIndex]
                // 立即设置背景色
                tagElement.style.backgroundColor = customColorScheme[colorIndex]
                tagElement.style.setProperty('background-color', customColorScheme[colorIndex], 'important')

                // 检查是否选中
                if (store.selectedTags.includes(tag.name)) {
                    tagElement.classList.add('selected')
                } else {
                    tagElement.classList.add('unselected')
                }

                // 添加点击事件
                tagElement.addEventListener('click', () => {
                    toggleTagSelection(tag.name)
                })

                tagsWrapper.appendChild(tagElement)
            })

            tagsContainer.value.appendChild(tagsWrapper)
            console.log('标签显示初始化完成')
        }

        // 切换标签选中状态
        const toggleTagSelection = (tagName) => {
            console.log('切换标签选中状态:', tagName)
            const currentSelectedTags = [...store.selectedTags]
            const tagIndex = currentSelectedTags.indexOf(tagName)

            if (tagIndex > -1) {
                // 如果已选中，则取消选中
                currentSelectedTags.splice(tagIndex, 1)
            } else {
                // 如果未选中，则添加到选中列表
                currentSelectedTags.push(tagName)
            }

            // 更新store中的选中标签
            store.setSelectedTags(currentSelectedTags)

            // 更新标签显示
            updateTagsDisplay()
        }

        // 更新标签显示
        const updateTagsDisplay = () => {
            if (!tagsContainer.value) return
            console.log('更新标签显示')
            const tagElements = tagsContainer.value.querySelectorAll('.tag-item')
            console.log('找到的标签元素数量:', tagElements.length)
            tagElements.forEach((tagElement, index) => {
                console.log(`标签${index}:`, tagElement)
                const tagName = tagElement.dataset.tagName
                const color = tagElement.dataset.color || '#4ECDC4'

                // 设置背景色
                tagElement.style.backgroundColor = color
                // 确保背景色不会被覆盖
                tagElement.style.setProperty('background-color', color, 'important')

                if (store.selectedTags.includes(tagName)) {
                    tagElement.classList.remove('unselected')
                    tagElement.classList.add('selected')
                    console.log(`标签${index}设置为选中状态`)
                } else {
                    tagElement.classList.remove('selected')
                    tagElement.classList.add('unselected')
                    console.log(`标签${index}设置为未选中状态`)
                }
            })
        }

        // 初始化分组显示
        const initGroups = () => {
            console.log('开始初始化分组显示')
            console.log('当前store中的分组:', store.groups)

            // 定义颜色方案
            const customColorScheme = [
                '#FF6B6B', '#4ECDC4', '#45B7D1', '#96CEB4', '#FFEAA7',
                '#DDA0DD', '#98D8C8', '#F7DC6F', '#BB8FCE', '#85C1E9',
                '#F8C471', '#82E0AA', '#F1948A', '#85C1E9', '#D7BDE2'
            ];

            if (!groupsContainer.value) {
                console.error('Groups container not found')
                return
            }

            // 清除之前的分组元素
            groupsContainer.value.innerHTML = ''

            // 创建分组容器
            const groupsWrapper = document.createElement('div')
            groupsWrapper.className = 'groups-wrapper'

            // 添加分组
            store.groups.forEach((group, index) => {
                console.log('处理分组:', group)
                // 确保分组对象有name属性
                if (!group || !group.name) {
                    console.warn('分组对象不完整:', group)
                    return
                }

                const groupElement = document.createElement('span')
                groupElement.className = 'group-item'
                groupElement.textContent = group.name
                groupElement.dataset.groupId = group.id || index
                groupElement.dataset.groupName = group.name

                const colorIndex = index % customColorScheme.length
                groupElement.dataset.color = group.color || customColorScheme[colorIndex]
                // 立即设置背景色
                groupElement.style.backgroundColor = group.color || customColorScheme[colorIndex]
                groupElement.style.setProperty('background-color', group.color || customColorScheme[colorIndex], 'important')

                // 检查是否选中
                if (store.filters.groups.includes(group.id)) {
                    groupElement.classList.add('selected')
                } else {
                    groupElement.classList.add('unselected')
                }

                // 添加点击事件
                groupElement.addEventListener('click', () => {
                    toggleGroupSelection(group.id)
                })

                groupsWrapper.appendChild(groupElement)
            })

            // 添加一个 "+" 按钮用于添加新分组
            const addGroupButton = document.createElement('span')
            addGroupButton.className = 'add-group-button'
            addGroupButton.textContent = '+'
            addGroupButton.title = '添加新分组'
            addGroupButton.addEventListener('click', () => {
                const groupName = prompt('请输入新分组的名称:')
                if (groupName) {
                    // 生成随机颜色
                    const randomColor = customColorScheme[Math.floor(Math.random() * customColorScheme.length)]

                    // 创建新分组对象
                    const newGroup = {
                        id: `group_${Date.now()}`,
                        name: groupName,
                        color: randomColor
                    }

                    // 添加到store
                    store.addGroup(newGroup)

                    // 重新初始化分组显示
                    initGroups()
                }
            })
            groupsWrapper.appendChild(addGroupButton)

            groupsContainer.value.appendChild(groupsWrapper)
            console.log('分组显示初始化完成')
        }

        // 切换分组选中状态
        const toggleGroupSelection = (groupId) => {
            console.log('切换分组选中状态:', groupId)
            const currentSelectedGroups = [...store.filters.groups]
            const groupIndex = currentSelectedGroups.indexOf(groupId)

            if (groupIndex > -1) {
                // 如果已选中，则取消选中
                currentSelectedGroups.splice(groupIndex, 1)
            } else {
                // 如果未选中，则添加到选中列表
                currentSelectedGroups.push(groupId)
            }
            console.log("currentSelectedGroups:", currentSelectedGroups)
            // 更新store中的选中分组
            store.setFilters({ groups: currentSelectedGroups })

            // 更新分组显示
            updateGroupsDisplay()

            // 刷新图形（可能需要根据选中的分组过滤节点）
            loadGraphData()
        }

        // 更新分组显示
        const updateGroupsDisplay = () => {
            if (!groupsContainer.value) return
            console.log('更新分组显示')
            const groupElements = groupsContainer.value.querySelectorAll('.group-item')
            console.log('找到的分组元素数量:', groupElements.length)
            groupElements.forEach((groupElement, index) => {
                console.log(`分组${index}:`, groupElement)
                const groupId = groupElement.dataset.groupId
                const color = groupElement.dataset.color || '#4ECDC4'

                // 设置背景色
                groupElement.style.backgroundColor = color
                // 确保背景色不会被覆盖
                groupElement.style.setProperty('background-color', color, 'important')

                if (store.filters.groups.includes(groupId)) {
                    groupElement.classList.remove('unselected')
                    groupElement.classList.add('selected')
                    console.log(`分组${index}设置为选中状态`)
                } else {
                    groupElement.classList.remove('selected')
                    groupElement.classList.add('unselected')
                    console.log(`分组${index}设置为未选中状态`)
                }
            })
        }

        onMounted(() => {
            nextTick(() => {
                initScene()
                loadGraphData()

                // 初始化标签和分组显示
                initTags()
                initGroups()

                window.addEventListener('resize', handleResize)

                // 组件挂载完成后自动适应视图
                setTimeout(() => {
                    fitView();
                }, 100); // 延迟执行以确保数据加载完成
            })
        })

        onUnmounted(() => {
            // 清理事件监听器
            window.removeEventListener('resize', handleResize)

            // 清理Three.js资源
            if (renderer) {
                renderer.domElement.removeEventListener('click', onMouseClick)
                renderer.domElement.removeEventListener('mousemove', onMouseMove)
                renderer.domElement.removeEventListener('mousedown', onMouseDown)
                renderer.domElement.removeEventListener('mousemove', onMouseMoveForDrag)
                renderer.domElement.removeEventListener('mouseup', onMouseUp)
                renderer.domElement.removeEventListener('mouseleave', onMouseUp)
            }

            if (controls) {
                controls.dispose()
            }

            if (renderer) {
                renderer.dispose()
            }
        })

        // 暴露方法给父组件
        return {
            graphContainer,
            tagsContainer,
            groupsContainer,
            loadGraphData,
            fitView,
            resetLayout,
            zoomIn,
            zoomOut,
            toggleFullscreen,
            selectNode,
            highlightRelatedNodes,
            resize,
            // 右键菜单相关方法
            setAsSourceNode,
            setAsTargetNode,
            addTagToNode,
            deleteNode,
            expandNode,
            editNode,
            toggleNodeLock,
            setAsTag,
            updateGroupMenu,
            updateTagMenu,
            isNodeInGroup,
            isNodeInTag,
            toggleNodeGroup,
            handleTagSelection
        }
    }
}
</script>

<style scoped>
.knowledge-graph-3d {
    width: 100%;
    height: 100%;
    position: relative;
    display: flex;
    flex-direction: column;
}

.tags-container {
    padding: 10px;
    background-color: #f8f9fa;
    border-bottom: 1px solid #e1e5eb;
    min-height: 20px;
    max-height: 100px;
    overflow-x: auto;
    overflow-y: hidden;
}

/* 分组容器样式 */
.groups-container {
    padding: 10px;
    background-color: #f0f0f0;
    border-bottom: 1px solid #e1e5eb;
    min-height: 20px;
    max-height: 100px;
    overflow-x: auto;
    overflow-y: hidden;
}

.tags-wrapper {
    display: flex;
    flex-wrap: wrap;
    gap: 8px;
}

.groups-wrapper {
    display: flex;
    flex-wrap: wrap;
    gap: 8px;
}

.graph-container-3d {
    width: 100%;
    height: 100%;
    flex: 1;
    position: relative;
}

.group-in {
    background-color: #e8f5e9;
}

.group-out {
    background-color: white;
}

.node-label-3d {
    position: absolute;
    top: 0;
    left: 0;
    padding: 4px 8px;
    border-radius: 4px;
    background-color: rgba(0, 0, 0, 0.7);
    color: white;
    font-size: 12px;
    white-space: nowrap;
    pointer-events: none;
    z-index: 1000;
    display: none;
    transform: translate(-50%, -100%);
}

/* 为动态创建的标签元素使用非scoped样式 */
:deep(.tag-item) {
    padding: 6px 12px;
    border-radius: 16px;
    font-size: 12px;
    font-weight: 500;
    cursor: pointer;
    transition: all 0.2s ease;
    color: white;
    border: 2px solid transparent;
    user-select: none;
    /* 确保背景色可以被JavaScript设置覆盖 */
    background-color: transparent !important;
}

:deep(.tag-item.selected) {
    border-color: #cab644;
    transform: scale(1.05);
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.2);
    opacity: 1;
    /* 不设置背景色，让JavaScript控制 */
}

:deep(.tag-item.unselected) {
    border-color: gray;
    opacity: 0.3;
    color: white;
    /* 不设置背景色，让JavaScript控制 */
}

:deep(.tag-item:hover) {
    opacity: 1;
    transform: scale(1.05);
}

/* 分组项样式 */
:deep(.group-item) {
    padding: 6px 12px;
    border-radius: 16px;
    font-size: 12px;
    font-weight: 500;
    cursor: pointer;
    transition: all 0.2s ease;
    color: white;
    border: 2px solid transparent;
    user-select: none;
    /* 确保背景色可以被JavaScript设置覆盖 */
    background-color: transparent !important;
}

:deep(.group-item.selected) {
    border-color: #cab644;
    transform: scale(1.05);
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.2);
    opacity: 1;
    /* 不设置背景色，让JavaScript控制 */
}

:deep(.group-item.unselected) {
    border-color: gray;
    opacity: 0.3;
    color: white;
    /* 不设置背景色，让JavaScript控制 */
}

:deep(.group-item:hover) {
    opacity: 1;
    transform: scale(1.05);
}

/* 添加分组按钮样式 */
:deep(.add-group-button) {
    padding: 6px 12px;
    border-radius: 16px;
    font-size: 16px;
    font-weight: 500;
    cursor: pointer;
    transition: all 0.2s ease;
    color: #333;
    border: 2px solid #ccc;
    user-select: none;
    background-color: #f0f0f0 !important;
    margin-left: 5px;
}

:deep(.add-group-button:hover) {
    background-color: #e0e0e0 !important;
    transform: scale(1.1);
}
</style>